import { Injectable } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { map } from 'rxjs/operators';

import { selectQueryParams, selectRouteParam } from './router.selectors';
import { NavigationsActions, NavigationUtilsActions } from './router.actions';
import { GlobalService } from '@modules/core/services';
import { ApplicationQueryParamsEnum, PathRouteNameEnum } from '@modules/core/enums';
import { filteringTrustValueInObject } from '@modules/core/utils';
import { selectStoreUuid } from '@modules/company-page/store/company-stores';

@Injectable()
export class NavigationsEffects {
  constructor(
    private actions$: Actions,
    private globalService: GlobalService,
    private store: Store,
    private router: Router,
    private route: ActivatedRoute,
    private global: GlobalService
  ) {}

  toThankYouPage$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(NavigationsActions.redirectToThankYouPage),
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        concatLatestFrom(({ appointmentUuid }) => [
          this.store.select(selectQueryParams),
          this.store.select(selectRouteParam('companyUuid')),
          this.store.select(selectStoreUuid),
        ]),
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        map(([{ appointmentUuid }, queryParams, companyUuid, storeUuid]) => {
          const params: Params = {
            ...this.filterQueryParams(queryParams),
            [ApplicationQueryParamsEnum.APPOINTMENT_UUID]: appointmentUuid,
          };
          this.router.navigate(
            [...this.getPathCustomDomain(companyUuid), PathRouteNameEnum.booking, PathRouteNameEnum.thankYou],
            { queryParams: params as Params, replaceUrl: true }
          );
        })
      );
    },
    { dispatch: false }
  );

  toPaymentClient$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(NavigationsActions.redirectToPaymentClient),
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        map(({ url }) => {
          window.location.assign(url);
        })
      );
    },
    { dispatch: false }
  );

  clearPaymentStatusUrl$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(NavigationUtilsActions.clearPaymentStatusURL),
        concatLatestFrom(() => [this.store.select(selectQueryParams)]),
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        map(([action, queryParams]): void => {
          const param = { ...queryParams };
          delete param[ApplicationQueryParamsEnum.PAYMENT_STATUS];

          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: param,
            replaceUrl: true,
          });
        })
      );
    },
    { dispatch: false }
  );

  private getPathCustomDomain(companyUuid: string | undefined): (string | undefined)[] {
    return this.globalService.hasCustomDomain && companyUuid ? [] : [companyUuid];
  }

  private filterQueryParams(params: Params): Params {
    const { storeUuid, tableUuid } = params;
    const filterParams = filteringTrustValueInObject({ storeUuid, tableUuid });
    return filterParams as Params;
  }
}
